import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { SEO } from "@/components/SEO"
import { OrganizationSchema, WebSiteSchema } from "@/components/schema"
import { VideoBackground } from "@/components/ui/VideoBackground"
import { Button } from "@/components/ui/button"
import { Link } from "react-router-dom"
import {
  ArrowRight,
  Brain,
  Shield,
  Lock,
  TrendingUp,
  Rocket,
  Lightbulb,
  Layers,
  Settings,
  Users,
  ExternalLink,
} from "lucide-react"
import { companies } from "@/data/companyData"

/* ───────────────────────────── DATA ───────────────────────────── */

const holdingServices = [
  {
    icon: Rocket,
    title: "Strategic Investment & Incubation",
    description:
      "Identifying, funding, and scaling the next generation of AI-native companies — from concept to market leadership.",
  },
  {
    icon: Layers,
    title: "Enterprise AI Solutions",
    description:
      "Through Connexr, we deliver managed services, AIOps, and AI transformation to Fortune 500 enterprises across 9 industries.",
  },
  {
    icon: Lightbulb,
    title: "Product Innovation",
    description:
      "Building LeoRix AI — our enterprise intelligence platform — alongside Connexr's managed services and AI transformation capabilities.",
  },
  {
    icon: Settings,
    title: "Shared Infrastructure & Operations",
    description:
      "SOC 2 certified, HIPAA compliant shared infrastructure, security operations, and compliance across all portfolio companies.",
  },
]

const differentiators = [
  {
    num: "01",
    icon: Brain,
    title: "AI-First Portfolio",
    description:
      "Every company in our portfolio is built with AI at its core — not bolted on as an afterthought. Intelligence is our foundation.",
  },
  {
    num: "02",
    icon: Shield,
    title: "End-to-End Ecosystem",
    description:
      "Connexr delivers enterprise managed services and AI transformation, while LeoRix AI powers intelligent orchestration across the stack.",
  },
  {
    num: "03",
    icon: Lock,
    title: "Enterprise-Grade Security",
    description:
      "SOC 2 certified, HIPAA compliant, and built for regulated industries. Security and compliance are engineered across all companies.",
  },
  {
    num: "04",
    icon: TrendingUp,
    title: "Outcome-Driven Innovation",
    description:
      "We measure success in business outcomes — reduced MTTR, lower TCO, faster time-to-market — not just products shipped.",
  },
]

const stats = [
  { value: "2", label: "Companies" },
  { value: "9", label: "Industries" },
  { value: "500+", label: "Implementations" },
  { value: "24/7", label: "Operations" },
]

/* ───────────────────────────── PAGE ───────────────────────────── */

const Index = () => {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      <SEO
        title="RSA Tech Group | The AI Holding Company"
        description="RSA Tech Group is the AI holding company behind Connexr and LeoRix AI — building the companies that power the intelligent enterprise."
        image="/digital-strategy-impressive-bg.png"
      />
      <OrganizationSchema />
      <WebSiteSchema />
      <Navigation />

      {/* ═══════════════════════════════════════════════════════════
          S1  HERO — Dark + Video Background
      ═══════════════════════════════════════════════════════════ */}
      <section data-nav-theme="dark" className="relative pb-28 overflow-hidden min-h-screen pt-32 sm:pt-36 lg:pt-40 flex items-center justify-center">
        <VideoBackground />

        {/* RSA brand radial gradients over video */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(28,43,110,0.5),transparent_70%)] z-30 pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_100%,rgba(28,43,110,0.3),transparent_70%)] z-30 pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_40%,rgba(253,164,0,0.06),transparent_70%)] z-30 pointer-events-none" />

        {/* Glow orbs */}
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#FDA400]/[0.03] rounded-full blur-[150px] z-30 pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#1C2B6E]/[0.15] rounded-full blur-[120px] z-30 pointer-events-none" />

        <div className="section-container relative z-40 text-center">
          <p className="text-[#FDA400] text-sm font-bold uppercase tracking-[0.3em] mb-6 drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
            RSA Tech Group
          </p>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white tracking-tight leading-[1.05] mb-6 max-w-5xl mx-auto">
            The <span className="text-[#FDA400]">AI</span> Holding
            <br className="hidden md:block" /> Company
          </h1>

          <p className="text-lg md:text-xl text-slate-300 leading-relaxed font-light max-w-2xl mx-auto mb-12 drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
            Building the companies that power the intelligent enterprise
          </p>

          <Link to="/companies">
            <Button
              size="lg"
              className="bg-[#FDA400] hover:bg-[#FDB944] text-[#1C2B6E] font-bold text-sm px-10 py-6 h-auto rounded-lg shadow-lg shadow-[#FDA400]/20 transition-all"
            >
              Explore Our Companies <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          S2  OUR COMPANIES — Light + Dot Grid
      ═══════════════════════════════════════════════════════════ */}
      <section data-nav-theme="light" className="relative py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.25]"
          style={{
            backgroundImage:
              "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

        <div className="section-container relative z-10">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-10 bg-[#FDA400]" />
              <span className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em]">
                Our Companies
              </span>
              <div className="h-px w-10 bg-[#FDA400]" />
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
              A Portfolio of{" "}
              <span className="text-[#FDA400]">Intelligence</span>
            </h2>
            <p className="text-lg text-slate-500 max-w-3xl mx-auto mt-4 font-light">
              Two companies. One mission. Powering the AI-driven enterprise.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {companies.map((company) => (
              <Link
                key={company.name}
                to={company.href}
                className="group relative block rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_25px_80px_rgba(0,0,0,0.15)]"
              >
                {/* Card background */}
                <div className="absolute inset-0 bg-white rounded-2xl border border-gray-100 group-hover:border-gray-200 transition-colors" />

                {/* Color accent top bar */}
                <div
                  className="absolute top-0 left-0 right-0 h-1 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10"
                  style={{ background: company.color }}
                />

                {/* Subtle glow */}
                <div
                  className="absolute -top-16 -right-16 w-[200px] h-[200px] rounded-full blur-[80px] opacity-0 group-hover:opacity-[0.08] transition-opacity duration-700"
                  style={{ background: company.color }}
                />

                <div className="relative z-10 p-7 h-full flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-xl font-bold text-[#1C2B6E]">
                      {company.name}
                    </h3>
                    {company.flagship && (
                      <span className="text-[8px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FDA400]/15 text-[#FDA400] border border-[#FDA400]/20">
                        Flagship
                      </span>
                    )}
                  </div>

                  <p
                    className="text-sm font-semibold mb-3"
                    style={{ color: company.color }}
                  >
                    {company.tagline}
                  </p>

                  <p className="text-slate-500 text-sm leading-relaxed mb-5 flex-1">
                    {company.description}
                  </p>

                  <div className="flex items-center justify-between">
                    {company.externalUrl ? (
                      <a
                        href={company.externalUrl}
                        target="_blank"
                        rel="noopener"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-[#FDA400] font-semibold hover:text-[#1C2B6E] transition-colors inline-flex items-center gap-1"
                      >
                        {company.domain} <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-xs text-slate-400 font-medium">
                        {company.domain}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-sm font-bold group-hover:gap-3 transition-all duration-300" style={{ color: company.color }}>
                      Visit <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/companies"
              className="inline-flex items-center gap-2 text-[#1C2B6E] font-bold text-sm hover:text-[#FDA400] transition-colors"
            >
              View All Companies <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          S3  WHAT RSA TECH DOES — Dark
      ═══════════════════════════════════════════════════════════ */}
      <section data-nav-theme="dark" className="relative py-24 bg-[#0f1629] overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(253,164,0,0.04),transparent_70%)]" />

        <div className="section-container relative z-10">
          <div className="text-center mb-16">
            <p className="text-[#FDA400] text-sm font-bold uppercase tracking-[0.2em] mb-4">
              What We Do
            </p>
            <h2 className="text-4xl md:text-5xl font-black text-white">
              The Holding Company{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FDA400] to-[#FDB944]">
                Advantage
              </span>
            </h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto mt-4 font-light">
              RSA Tech Group provides strategic direction, shared infrastructure, and operational excellence across our portfolio of AI companies.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {holdingServices.map((service, i) => (
              <div
                key={service.title}
                className="group relative rounded-2xl bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] hover:border-[#FDA400]/20 p-8 transition-all duration-500 hover:-translate-y-1 overflow-hidden"
              >
                {/* Ghost number */}
                <div className="absolute top-3 right-4 text-[72px] font-black text-white/[0.03] leading-none select-none pointer-events-none">
                  {String(i + 1).padStart(2, "0")}
                </div>

                {/* Shine sweep on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none overflow-hidden">
                  <div className="absolute inset-0 animate-[shine_1.5s_ease-in-out]" style={{ background: "linear-gradient(105deg, transparent 40%, rgba(253,164,0,0.06) 45%, rgba(253,164,0,0.12) 50%, rgba(253,164,0,0.06) 55%, transparent 60%)" }} />
                </div>

                {/* Glow */}
                <div className="absolute -bottom-8 -right-8 w-28 h-28 rounded-full bg-[#FDA400]/0 group-hover:bg-[#FDA400]/10 blur-[60px] transition-all duration-700" />

                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center mb-5 group-hover:bg-[#FDA400]/10 transition-colors duration-300">
                    <service.icon className="w-6 h-6 text-[#FDA400]" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed text-sm">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          S4  WHY RSA TECH / DIFFERENTIATORS — Light
      ═══════════════════════════════════════════════════════════ */}
      <section data-nav-theme="light" className="relative py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.25]"
          style={{
            backgroundImage:
              "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

        <div className="section-container relative z-10">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-10 bg-[#FDA400]" />
              <span className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em]">
                Why RSA Tech Group
              </span>
              <div className="h-px w-10 bg-[#FDA400]" />
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
              What Sets Us{" "}
              <span className="text-[#FDA400]">Apart</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {differentiators.map((d) => (
              <div
                key={d.num}
                className="group partner-card relative rounded-2xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(28,43,110,0.12)] transition-all duration-500 hover:-translate-y-2"
              >
                {/* Top accent */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />

                {/* Ghost number */}
                <div className="absolute top-3 right-4 text-[60px] font-black text-[#1C2B6E]/[0.04] leading-none select-none pointer-events-none">
                  {d.num}
                </div>

                {/* Glow */}
                <div className="absolute -bottom-12 -right-12 w-44 h-44 bg-[#FDA400]/[0.04] rounded-full blur-[50px] group-hover:bg-[#FDA400]/10 group-hover:scale-110 transition-all duration-700" />

                <div className="p-7 h-full flex flex-col relative z-10">
                  <div className="w-12 h-12 rounded-xl bg-[#1C2B6E]/[0.08] flex items-center justify-center mb-5 group-hover:bg-[#1C2B6E] transition-colors duration-300">
                    <d.icon className="w-5 h-5 text-[#1C2B6E] group-hover:text-[#FDA400] transition-colors duration-300" />
                  </div>
                  <h3 className="text-base font-bold text-[#1C2B6E] mb-2">
                    {d.title}
                  </h3>
                  <p className="text-slate-500 leading-relaxed text-sm">
                    {d.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          S5  STATS BANNER — Dark
      ═══════════════════════════════════════════════════════════ */}
      <section data-nav-theme="dark" className="relative py-20 bg-[#0f1629] overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_50%,rgba(253,164,0,0.06),transparent_70%)]" />

        <div className="section-container relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 max-w-4xl mx-auto text-center">
            {stats.map((stat) => (
              <div key={stat.label}>
                <div className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FDA400] to-[#FDB944] mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-slate-400 uppercase tracking-wider font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          S6  CAREERS CTA — Light
      ═══════════════════════════════════════════════════════════ */}
      <section data-nav-theme="light" className="relative py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.25]"
          style={{
            backgroundImage:
              "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
            backgroundSize: "28px 28px",
          }}
        />

        <div className="section-container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-10 bg-[#FDA400]" />
              <span className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em]">
                Careers
              </span>
              <div className="h-px w-10 bg-[#FDA400]" />
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1] mb-6">
              Join One of Our{" "}
              <span className="text-[#FDA400]">Companies</span>
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-10 font-light">
              Build your career across our portfolio of AI companies. From enterprise services to startup acceleration, find your place in the intelligent enterprise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/careers"
                className="inline-flex items-center justify-center gap-2 bg-[#1C2B6E] text-white px-8 py-4 rounded-lg font-bold text-base hover:bg-[#162258] transition-colors"
              >
                <Users className="w-5 h-5" />
                View Open Positions
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          S7  CTA — Dark
      ═══════════════════════════════════════════════════════════ */}
      <section data-nav-theme="dark" className="relative py-24 bg-[#0f1629] overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(253,164,0,0.08),transparent_70%)]" />
        <div className="absolute top-1/3 left-0 w-[400px] h-[400px] bg-[#FDA400]/[0.03] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-[#1C2B6E]/[0.2] rounded-full blur-[120px]" />

        <div className="section-container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="relative rounded-3xl bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] p-10 md:p-16 overflow-hidden">
              {/* Inner glow */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] h-[200px] bg-[#FDA400]/[0.06] rounded-full blur-[80px]" />

              <div className="relative z-10">
                <h2 className="text-3xl md:text-5xl font-black text-white mb-6 leading-tight">
                  Partner with
                  <br /> <span className="text-[#FDA400]">RSA Tech Group</span>
                </h2>
                <p className="text-lg text-slate-400 max-w-xl mx-auto mb-10">
                  Whether you're an enterprise seeking AI transformation or a founder building the next breakthrough — let's talk.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 bg-[#FDA400] text-[#1C2B6E] px-8 py-4 rounded-full font-bold text-base hover:bg-[#FDB944] transition-colors"
                  >
                    Get in Touch
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                  <Link
                    to="/companies"
                    className="inline-flex items-center justify-center gap-2 border-2 border-white/20 text-white px-8 py-4 rounded-full font-bold text-base hover:border-white/40 transition-colors"
                  >
                    Explore Our Companies
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Index
